import { useEffect, useState, useCallback } from 'react';
import { Plus, Trash2, GripVertical, Loader2, Star, ImageIcon, Search } from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { featuredCategoriesApi, categoriesApi } from '@/services/api';
import type { FeaturedCategoryItem, Category } from '@/types';
import { toast } from 'sonner';

interface SortableRowProps {
  item: FeaturedCategoryItem;
  index: number;
  saving: boolean;
  onRemove: (id: string) => void;
}

const SortableRow = ({ item, index, saving, onRemove }: SortableRowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-gray-50 bg-white">
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="flex items-center gap-3">
          <button
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 text-gray-400 hover:text-gray-600"
            disabled={saving}
          >
            <GripVertical className="w-5 h-5" />
          </button>
          <span className="text-gray-500 font-medium w-6">{index + 1}</span>
        </div>
      </td>
      <td className="px-6 py-4">
        <div className="flex items-center gap-3">
          {item.category.image?.url ? (
            <img
              src={item.category.image.url}
              alt={item.category.name}
              className="w-12 h-12 object-cover rounded-lg"
            />
          ) : (
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-gray-400" />
            </div>
          )}
          <div>
            <div className="font-medium text-gray-900">{item.category.name}</div>
            <div className="text-sm text-gray-500">{item.category.slug}</div>
          </div>
        </div>
      </td>
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <button
          onClick={() => onRemove(item.id)}
          disabled={saving}
          className="text-red-600 hover:text-red-800 disabled:opacity-50"
          title="Kaldır"
        >
          <Trash2 className="w-5 h-5" />
        </button>
      </td>
    </tr>
  );
};

export const FeaturedCategoriesPage = () => {
  const [featuredCategories, setFeaturedCategories] = useState<FeaturedCategoryItem[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>('');

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const [featured, categories] = await Promise.all([
        featuredCategoriesApi.getAll(),
        categoriesApi.getAll(),
      ]);
      setFeaturedCategories(featured);
      // Filter to only show visible root categories that aren't already featured
      setAllCategories(categories.filter(c => !c.hidden && !c.parentId));
    } catch (err) {
      console.error('Veri yüklenemedi:', err);
      setError('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Get available categories (not already featured) and filter by search
  const availableCategories = allCategories.filter(
    cat => !featuredCategories.some(fc => fc.categoryId === cat.id)
  );

  const filteredCategories = availableCategories.filter(cat =>
    cat.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleAddFeatured = async () => {
    if (!selectedCategoryId) return;

    try {
      setSaving(true);
      setError('');
      await featuredCategoriesApi.add(selectedCategoryId);
      toast.success('Kategori öne çıkarıldı');
      setModalOpen(false);
      setSelectedCategoryId('');
      await loadData();
    } catch (err: any) {
      setError(err.message || 'Kategori öne çıkarılamadı');
      toast.error(err.message || 'Kategori öne çıkarılamadı');
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id: string) => {
    if (!confirm('Bu kategoriyi öne çıkanlardan kaldırmak istediğinize emin misiniz?')) {
      return;
    }

    try {
      setSaving(true);
      setError('');
      await featuredCategoriesApi.remove(id);
      toast.success('Kategori öne çıkanlardan kaldırıldı');
      await loadData();
    } catch (err: any) {
      setError(err.message || 'İşlem başarısız');
      toast.error(err.message || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) {
      return;
    }

    const oldIndex = featuredCategories.findIndex((item) => item.id === active.id);
    const newIndex = featuredCategories.findIndex((item) => item.id === over.id);

    // Optimistic update
    const newOrder = arrayMove(featuredCategories, oldIndex, newIndex);
    setFeaturedCategories(newOrder);

    try {
      setSaving(true);
      setError('');
      // Send bulk update with new order
      const categoryIds = newOrder.map((item) => item.categoryId);
      await featuredCategoriesApi.bulkUpdate(categoryIds);
    } catch (err: any) {
      setError(err.message || 'Sıralama güncellenemedi');
      toast.error(err.message || 'Sıralama güncellenemedi');
      // Revert on error
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Öne Çıkan Kategoriler</h1>
          <p className="text-gray-600 mt-1">
            Ana sayfada görünecek kategorileri seçin ve sıralayın
          </p>
        </div>
        <button
          onClick={() => setModalOpen(true)}
          disabled={availableCategories.length === 0}
          className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="w-5 h-5" />
          Kategori Ekle
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Featured Categories List */}
      {featuredCategories.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
          <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Henüz öne çıkan kategori yok
          </h3>
          <p className="text-gray-600 mb-4">
            Ana sayfada gösterilecek kategorileri ekleyin
          </p>
          <button
            onClick={() => setModalOpen(true)}
            disabled={availableCategories.length === 0}
            className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50"
          >
            <Plus className="w-5 h-5" />
            Kategori Ekle
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sıra
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Kategori
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    İşlemler
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <SortableContext
                  items={featuredCategories.map((item) => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {featuredCategories.map((item, index) => (
                    <SortableRow
                      key={item.id}
                      item={item}
                      index={index}
                      saving={saving}
                      onRemove={handleRemove}
                    />
                  ))}
                </SortableContext>
              </tbody>
            </table>
          </DndContext>
        </div>
      )}

      {/* Add Category Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b">
              <h2 className="text-lg font-semibold">Kategori Öne Çıkar</h2>
              <button
                onClick={() => {
                  setModalOpen(false);
                  setSelectedCategoryId('');
                  setSearchQuery('');
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
              >
                &times;
              </button>
            </div>

            <div className="p-4 flex-1 overflow-hidden flex flex-col">
              {availableCategories.length === 0 ? (
                <p className="text-gray-600 text-center py-4">
                  Tüm kategoriler zaten öne çıkarılmış.
                </p>
              ) : (
                <div className="space-y-4 flex-1 flex flex-col overflow-hidden">
                  {/* Search Input */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Kategori ara..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-black"
                    />
                  </div>

                  {/* Category List */}
                  <div className="flex-1 overflow-y-auto border border-gray-200 rounded-lg max-h-[300px]">
                    {filteredCategories.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">
                        {searchQuery ? 'Kategori bulunamadı' : 'Kategori yok'}
                      </p>
                    ) : (
                      <div className="divide-y divide-gray-100">
                        {filteredCategories.map((cat) => (
                          <button
                            key={cat.id}
                            onClick={() => setSelectedCategoryId(cat.id)}
                            className={`w-full flex items-center gap-3 p-3 text-left hover:bg-gray-50 transition-colors ${
                              selectedCategoryId === cat.id ? 'bg-gray-100' : ''
                            }`}
                          >
                            {cat.image?.url ? (
                              <img
                                src={cat.image.url}
                                alt={cat.name}
                                className="w-10 h-10 object-cover rounded"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-gray-100 rounded flex items-center justify-center">
                                <ImageIcon className="w-5 h-5 text-gray-400" />
                              </div>
                            )}
                            <span className="font-medium text-gray-900 truncate">
                              {cat.name}
                            </span>
                            {selectedCategoryId === cat.id && (
                              <span className="ml-auto text-black">✓</span>
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end gap-3 pt-4 border-t">
                    <button
                      onClick={() => {
                        setModalOpen(false);
                        setSelectedCategoryId('');
                        setSearchQuery('');
                      }}
                      className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                    >
                      İptal
                    </button>
                    <button
                      onClick={handleAddFeatured}
                      disabled={!selectedCategoryId || saving}
                      className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                      Ekle
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeaturedCategoriesPage;
